import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaBriefcase,
  FaCalendarAlt,
  FaEdit,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaProjectDiagram,
  FaStickyNote,
  FaTrash,
  FaUser,
} from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import PortalDomainSelect from "./PortalDomainSelect";
import PortalClientAttachments from "./PortalClientAttachments";
import { canDeleteInPortal } from "./portalSession";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";

const CLIENT_STATUSES = ["Active", "Inactive"];
const SERVICE_STATUSES = ["Active", "Inactive"];
const PROJECT_STATUSES = ["Active", "Inactive"];
const EMPTY_SERVICE_FORM = {
  serviceName: "",
  description: "",
  status: "Active",
};
const EMPTY_PROJECT_FORM = {
  projectName: "",
  shortCode: "",
  description: "",
  status: "Active",
};

function displayValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function DetailItem({ label, value, children }) {
  return (
    <div className="cportal-profile-view-item">
      <span className="cportal-profile-view-label">{label}</span>
      <span className="cportal-profile-view-value">{children ?? displayValue(value)}</span>
    </div>
  );
}

function DetailSection({ icon: Icon, title, children, className = "", actions = null }) {
  return (
    <section className={`cportal-card cportal-detail-section ${className}`.trim()}>
      <div className="cportal-detail-section-head">
        <h2 className="cportal-detail-section-title">
          {Icon ? <Icon className="cportal-detail-section-icon" aria-hidden="true" /> : null}
          {title}
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

function clientInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "C";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "cportal-badge cportal-badge--success";
  if (s === "inactive") return "cportal-badge cportal-badge--danger";
  return "cportal-badge";
}

function toForm(row) {
  return {
    clientName: row?.Client_Name || "",
    contactPerson: row?.Contact_Person || "",
    mobileNumber: row?.Mobile_Number || "",
    altNumber: row?.Alt_Number || "",
    email: row?.Email || "",
    address: row?.Address || "",
    website: row?.Website || "",
    domain: row?.Domain || "",
    description: row?.Description || "",
    status: row?.Status || "Active",
  };
}

function buildPayload(form) {
  return {
    Client_Name: form.clientName.trim(),
    Contact_Person: form.contactPerson.trim() || null,
    Mobile_Number: form.mobileNumber.trim() || null,
    Alt_Number: form.altNumber.trim() || null,
    Email: form.email.trim() || null,
    Address: form.address.trim() || null,
    Website: form.website.trim() || null,
    Domain: form.domain.trim() || null,
    Description: form.description.trim() || null,
    Status: form.status || "Active",
  };
}

function toServiceForm(row) {
  return {
    serviceName: row?.Service_Name || "",
    description: row?.Description || "",
    status: row?.Status || "Active",
  };
}

function buildServicePayload(form) {
  return {
    Service_Name: form.serviceName.trim(),
    Description: form.description.trim() || null,
    Status: form.status || "Active",
  };
}

function toProjectForm(row) {
  return {
    projectName: row?.Project_Name || "",
    shortCode: row?.Short_Code || "",
    description: row?.Description || "",
    status: row?.Status || "Active",
  };
}

function buildProjectPayload(form) {
  return {
    Project_Name: form.projectName.trim(),
    Short_Code: form.shortCode.trim(),
    Description: form.description.trim() || null,
    Status: form.status || "Active",
  };
}

function CustomerTenantClientView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [client, setClient] = useState(null);
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [serviceForm, setServiceForm] = useState(EMPTY_SERVICE_FORM);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [serviceSubmitting, setServiceSubmitting] = useState(false);
  const [serviceFormError, setServiceFormError] = useState("");
  const [deactivatingServiceId, setDeactivatingServiceId] = useState(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projectForm, setProjectForm] = useState(EMPTY_PROJECT_FORM);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [projectSubmitting, setProjectSubmitting] = useState(false);
  const [projectFormError, setProjectFormError] = useState("");
  const [deactivatingProjectId, setDeactivatingProjectId] = useState(null);
  const [activeSection, setActiveSection] = useState("details");
  const canDelete = useMemo(() => canDeleteInPortal(), []);

  const loadClient = useCallback(async () => {
    const res = await axiosInstance.get(`/client/tenant-clients/${id}`);
    const data = res.data?.data || null;
    setClient(data);
    setServices(Array.isArray(data?.Services) ? data.Services : []);
    setProjects(Array.isArray(data?.Projects) ? data.Projects : []);
    setForm(toForm(data));
    return data;
  }, [id]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await loadClient();
        if (!active) return;
        if (!data) setError("Client not found.");
      } catch (err) {
        if (!active) return;
        setError(err.response?.data?.message || "Could not load client details.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [loadClient]);

  useEffect(() => {
    if (location.state?.edit) {
      setEditing(true);
    }
  }, [location.state?.edit]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const setServiceField = (name, value) => setServiceForm((f) => ({ ...f, [name]: value }));
  const setProjectField = (name, value) => setProjectForm((f) => ({ ...f, [name]: value }));

  const closeServiceModal = () => {
    setShowServiceModal(false);
    setEditingServiceId(null);
    setServiceForm(EMPTY_SERVICE_FORM);
    setServiceFormError("");
  };

  const openAddServiceModal = () => {
    setEditingServiceId(null);
    setServiceForm(EMPTY_SERVICE_FORM);
    setServiceFormError("");
    setShowServiceModal(true);
  };

  const openEditServiceModal = (service) => {
    setEditingServiceId(service.Tenant_Client_Service_Id);
    setServiceForm(toServiceForm(service));
    setServiceFormError("");
    setShowServiceModal(true);
  };

  const closeProjectModal = () => {
    setShowProjectModal(false);
    setEditingProjectId(null);
    setProjectForm(EMPTY_PROJECT_FORM);
    setProjectFormError("");
  };

  const openAddProjectModal = () => {
    setEditingProjectId(null);
    setProjectForm(EMPTY_PROJECT_FORM);
    setProjectFormError("");
    setShowProjectModal(true);
  };

  const openEditProjectModal = (project) => {
    setEditingProjectId(project.Tenant_Project_Id);
    setProjectForm(toProjectForm(project));
    setProjectFormError("");
    setShowProjectModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.clientName.trim()) {
      setMessage({ type: "error", text: "Client name is required." });
      return;
    }
    if (!form.contactPerson.trim()) {
      setMessage({ type: "error", text: "Contact person is required." });
      return;
    }

    const mobileError = mobileValidationMessage(form.mobileNumber, {
      required: true,
      label: "Mobile number",
    });
    if (mobileError) {
      setMessage({ type: "error", text: mobileError });
      return;
    }
    const altNumberError = mobileValidationMessage(form.altNumber, { label: "Alternate number" });
    if (altNumberError) {
      setMessage({ type: "error", text: altNumberError });
      return;
    }

    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await axiosInstance.put(`/client/tenant-clients/${id}`, buildPayload(form));
      const data = res.data?.data;
      if (data) {
        setClient(data);
        setServices(Array.isArray(data.Services) ? data.Services : []);
        setProjects(Array.isArray(data.Projects) ? data.Projects : []);
        setForm(toForm(data));
      }
      setEditing(false);
      setMessage({ type: "success", text: "Client updated." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not update client.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;

    setDeleting(true);
    setMessage({ type: "", text: "" });
    try {
      await axiosInstance.delete(`/client/tenant-clients/${id}`);
      navigate("/portal/clients");
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not deactivate client.",
      });
      setDeleting(false);
    }
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    if (!serviceForm.serviceName.trim()) {
      setServiceFormError("Service name is required.");
      return;
    }

    setServiceSubmitting(true);
    setServiceFormError("");
    try {
      if (editingServiceId) {
        await axiosInstance.put(
          `/client/tenant-clients/${id}/services/${editingServiceId}`,
          buildServicePayload(serviceForm)
        );
      } else {
        await axiosInstance.post(
          `/client/tenant-clients/${id}/services`,
          buildServicePayload(serviceForm)
        );
      }
      await loadClient();
      closeServiceModal();
      setMessage({ type: "success", text: editingServiceId ? "Service updated." : "Service added." });
    } catch (err) {
      setServiceFormError(err.response?.data?.message || "Could not save service.");
    } finally {
      setServiceSubmitting(false);
    }
  };

  const handleDeactivateService = async (service) => {
    if (service.Status === "Inactive") return;
    if (!window.confirm(`Deactivate ${service.Service_Name}?`)) return;

    setDeactivatingServiceId(service.Tenant_Client_Service_Id);
    setMessage({ type: "", text: "" });
    try {
      await axiosInstance.delete(
        `/client/tenant-clients/${id}/services/${service.Tenant_Client_Service_Id}`
      );
      await loadClient();
      setMessage({ type: "success", text: "Service deactivated." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not deactivate service.",
      });
    } finally {
      setDeactivatingServiceId(null);
    }
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    if (!projectForm.projectName.trim()) {
      setProjectFormError("Project name is required.");
      return;
    }
    if (!projectForm.shortCode.trim()) {
      setProjectFormError("Short code is required.");
      return;
    }

    setProjectSubmitting(true);
    setProjectFormError("");
    try {
      if (editingProjectId) {
        await axiosInstance.put(
          `/client/tenant-clients/${id}/projects/${editingProjectId}`,
          buildProjectPayload(projectForm)
        );
      } else {
        await axiosInstance.post(
          `/client/tenant-clients/${id}/projects`,
          buildProjectPayload(projectForm)
        );
      }
      await loadClient();
      closeProjectModal();
      setMessage({ type: "success", text: editingProjectId ? "Project updated." : "Project added." });
    } catch (err) {
      setProjectFormError(err.response?.data?.message || "Could not save project.");
    } finally {
      setProjectSubmitting(false);
    }
  };

  const handleDeactivateProject = async (project) => {
    if (project.Status === "Inactive") return;
    if (!window.confirm(`Deactivate ${project.Project_Name}?`)) return;

    setDeactivatingProjectId(project.Tenant_Project_Id);
    setMessage({ type: "", text: "" });
    try {
      await axiosInstance.delete(
        `/client/tenant-clients/${id}/projects/${project.Tenant_Project_Id}`
      );
      await loadClient();
      setMessage({ type: "success", text: "Project deactivated." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not deactivate project.",
      });
    } finally {
      setDeactivatingProjectId(null);
    }
  };

  if (loading) {
    return (
      <div className="cportal-page">
        <p className="cportal-muted">Loading…</p>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="cportal-page">
        <button
          type="button"
          className="cportal-back-link"
          onClick={() => navigate("/portal/clients")}
        >
          <FaArrowLeft /> Back to clients
        </button>
        <div className="cportal-alert cportal-alert-error">{error || "Client not found."}</div>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="cportal-page">
        <button
          type="button"
          className="cportal-back-link"
          onClick={() => {
            setEditing(false);
            setForm(toForm(client));
            setMessage({ type: "", text: "" });
          }}
        >
          <FaArrowLeft /> Back to client
        </button>

        <form className="cportal-card" style={{ marginTop: 16 }} onSubmit={handleSave}>
          <div className="cportal-card-head">
            <h2>Edit client</h2>
          </div>
          {message.text && (
            <div className={`cportal-alert cportal-alert-${message.type === "error" ? "error" : "success"}`}>
              {message.text}
            </div>
          )}
          <div className="cportal-form-grid cportal-form-grid--3">
            <div className="cportal-field">
              <label>Client name <span className="cportal-required">*</span></label>
              <input
                value={form.clientName}
                onChange={(e) => setField("clientName", e.target.value)}
                required
              />
            </div>
            <div className="cportal-field">
              <label>
                Contact person <span className="cportal-required">*</span>
              </label>
              <input
                value={form.contactPerson}
                onChange={(e) => setField("contactPerson", e.target.value)}
                required
              />
            </div>
            <div className="cportal-field">
              <label>
                Mobile number <span className="cportal-required">*</span>
              </label>
              <input
                value={form.mobileNumber}
                onChange={(e) => setField("mobileNumber", sanitizeMobileInput(e.target.value))}
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit number"
                required
              />
            </div>
            <div className="cportal-field">
              <label>Alt number</label>
              <input
                value={form.altNumber}
                onChange={(e) => setField("altNumber", sanitizeMobileInput(e.target.value))}
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit number"
              />
            </div>
            <div className="cportal-field">
              <label>Email</label>
              <input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} />
            </div>
            <div className="cportal-field">
              <label>Website</label>
              <input value={form.website} onChange={(e) => setField("website", e.target.value)} />
            </div>
            <PortalDomainSelect value={form.domain} onChange={(value) => setField("domain", value)} />
            <div className="cportal-field">
              <label>
                Status <span className="cportal-required">*</span>
              </label>
              <select value={form.status} onChange={(e) => setField("status", e.target.value)}>
                {CLIENT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="cportal-form-grid cportal-form-grid--3">
            <div className="cportal-field cportal-field--full">
              <label>Address</label>
              <textarea rows={3} value={form.address} onChange={(e) => setField("address", e.target.value)} />
            </div>
            <div className="cportal-field cportal-field--full">
              <label>Description</label>
              <textarea rows={3} value={form.description} onChange={(e) => setField("description", e.target.value)} />
            </div>
          </div>

          <div className="cportal-form-actions">
            <button
              type="button"
              className="cportal-btn cportal-btn-ghost"
              onClick={() => {
                setEditing(false);
                setForm(toForm(client));
                setMessage({ type: "", text: "" });
              }}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="cportal-btn cportal-btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="cportal-page">
      <button
        type="button"
        className="cportal-back-link"
        onClick={() => navigate("/portal/clients")}
      >
        <FaArrowLeft /> Back to clients
      </button>

      {message.text && (
        <div className={`cportal-alert cportal-alert-${message.type === "error" ? "error" : "success"}`}>
          {message.text}
        </div>
      )}

      <div className="cportal-entity-hero">
        <div className="cportal-entity-hero-left">
          <div className="cportal-entity-avatar" aria-hidden="true">
            {clientInitials(client.Client_Name)}
          </div>
          <div className="cportal-entity-hero-meta">
            <h1 className="cportal-entity-hero-name">{displayValue(client.Client_Name)}</h1>
            <span className={statusBadgeClass(client.Status)}>
              {displayValue(client.Status)}
            </span>
          </div>
        </div>
        <div className="cportal-entity-hero-actions">
          <button
            type="button"
            className="cportal-btn cportal-btn-ghost"
            onClick={() => setEditing(true)}
          >
            <FaEdit aria-hidden="true" /> Edit
          </button>
          {canDelete && (
            <button
              type="button"
              className="cportal-btn cportal-btn-danger-outline"
              onClick={handleDelete}
              disabled={deleting}
            >
              <FaTrash aria-hidden="true" />
              {deleting ? "Deactivating…" : "Delete"}
            </button>
          )}
        </div>
      </div>

      <nav className="cportal-profile-nav" aria-label="Client sections">
        <button
          type="button"
          className={`cportal-profile-nav-link${activeSection === "details" ? " active" : ""}`}
          aria-current={activeSection === "details" ? "page" : undefined}
          onClick={() => setActiveSection("details")}
        >
          Details
        </button>
        <button
          type="button"
          className={`cportal-profile-nav-link${activeSection === "projects" ? " active" : ""}`}
          aria-current={activeSection === "projects" ? "page" : undefined}
          onClick={() => setActiveSection("projects")}
        >
          Projects
        </button>
        <button
          type="button"
          className={`cportal-profile-nav-link${activeSection === "services" ? " active" : ""}`}
          aria-current={activeSection === "services" ? "page" : undefined}
          onClick={() => setActiveSection("services")}
        >
          Services
        </button>
      </nav>

      {activeSection === "details" && (
        <>
          <div className="cportal-detail-grid">
            <DetailSection icon={FaUser} title="Contact information">
              <div className="cportal-profile-view-grid">
                <DetailItem label="Client name" value={client.Client_Name} />
                <DetailItem label="Contact person" value={client.Contact_Person} />
                <DetailItem label="Mobile number" value={client.Mobile_Number} />
                <DetailItem label="Alt number" value={client.Alt_Number} />
                <DetailItem label="Email" value={client.Email} />
              </div>
            </DetailSection>

            <DetailSection icon={FaMapMarkerAlt} title="Address & business">
              <div className="cportal-profile-view-grid">
                <DetailItem label="Address" value={client.Address} />
                <DetailItem label="Website" value={client.Website} />
                <DetailItem label="Business" value={client.Domain} />
              </div>
            </DetailSection>
          </div>

          <DetailSection icon={FaInfoCircle} title="Other details">
            <div className="cportal-profile-view-grid">
              <DetailItem label="Created on">
                <span className="cportal-datetime-value">
                  <FaCalendarAlt aria-hidden="true" />
                  {formatDateTime(client.Created_On)}
                </span>
              </DetailItem>
              <DetailItem label="Updated on">
                <span className="cportal-datetime-value">
                  <FaCalendarAlt aria-hidden="true" />
                  {formatDateTime(client.Updated_On)}
                </span>
              </DetailItem>
            </div>
          </DetailSection>

          <DetailSection icon={FaStickyNote} title="Description">
            <div className="cportal-profile-view-grid">
              <DetailItem label="Description" value={client.Description} />
            </div>
          </DetailSection>
        </>
      )}

      {activeSection === "projects" && (
        <>
          <DetailSection
            icon={FaProjectDiagram}
            title="Projects"
            actions={
              <button type="button" className="cportal-btn cportal-btn-primary" onClick={openAddProjectModal}>
                Add project
              </button>
            }
          >
            {projects.length === 0 ? (
              <p className="cportal-muted">No projects added yet.</p>
            ) : (
              <div className="cportal-table-wrap">
                <table className="cportal-table">
                  <thead>
                    <tr>
                      <th>Project name</th>
                      <th>Short code</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th className="cportal-th-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project) => (
                      <tr key={project.Tenant_Project_Id}>
                        <td>{displayValue(project.Project_Name)}</td>
                        <td>{displayValue(project.Short_Code)}</td>
                        <td>{displayValue(project.Description)}</td>
                        <td>{displayValue(project.Status)}</td>
                        <td>
                          <div className="cportal-re-actions">
                            <button
                              type="button"
                              className="re-lead-action re-lead-action--edit"
                              onClick={() => openEditProjectModal(project)}
                              title="Edit project"
                              aria-label="Edit project"
                            >
                              <FaEdit size={16} />
                            </button>
                            {canDelete && (
                              <button
                                type="button"
                                className="re-lead-action re-lead-action--delete"
                                onClick={() => handleDeactivateProject(project)}
                                disabled={
                                  project.Status === "Inactive" ||
                                  deactivatingProjectId === project.Tenant_Project_Id
                                }
                                title={
                                  project.Status === "Inactive"
                                    ? "Already inactive"
                                    : "Deactivate project"
                                }
                                aria-label="Deactivate project"
                              >
                                {deactivatingProjectId === project.Tenant_Project_Id ? "…" : <FaTrash size={16} />}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </DetailSection>

          <PortalClientAttachments tenantClientId={id} scope="project" projects={projects} />
        </>
      )}

      {activeSection === "services" && (
        <>
          <DetailSection
            icon={FaBriefcase}
            title="Services"
            actions={
              <button type="button" className="cportal-btn cportal-btn-primary" onClick={openAddServiceModal}>
                Add service
              </button>
            }
          >
            {services.length === 0 ? (
              <p className="cportal-muted">No services added yet.</p>
            ) : (
              <div className="cportal-table-wrap">
                <table className="cportal-table">
                  <thead>
                    <tr>
                      <th>Service name</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th className="cportal-th-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((service) => (
                      <tr key={service.Tenant_Client_Service_Id}>
                        <td>{displayValue(service.Service_Name)}</td>
                        <td>{displayValue(service.Description)}</td>
                        <td>{displayValue(service.Status)}</td>
                        <td>
                          <div className="cportal-re-actions">
                            <button
                              type="button"
                              className="re-lead-action re-lead-action--edit"
                              onClick={() => openEditServiceModal(service)}
                              title="Edit service"
                              aria-label="Edit service"
                            >
                              <FaEdit size={16} />
                            </button>
                            {canDelete && (
                              <button
                                type="button"
                                className="re-lead-action re-lead-action--delete"
                                onClick={() => handleDeactivateService(service)}
                                disabled={
                                  service.Status === "Inactive" ||
                                  deactivatingServiceId === service.Tenant_Client_Service_Id
                                }
                                title={
                                  service.Status === "Inactive"
                                    ? "Already inactive"
                                    : "Deactivate service"
                                }
                                aria-label="Deactivate service"
                              >
                                {deactivatingServiceId === service.Tenant_Client_Service_Id ? "…" : <FaTrash size={16} />}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </DetailSection>

          <PortalClientAttachments tenantClientId={id} scope="service" services={services} />
        </>
      )}

      {showServiceModal && (
        <div className="cportal-modal-overlay" onClick={closeServiceModal} role="presentation">
          <div
            className="cportal-modal cportal-modal--form"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="client-service-modal-title"
          >
            <div className="cportal-modal-header">
              <h3 id="client-service-modal-title">
                {editingServiceId ? "Edit service" : "Add service"}
              </h3>
              <button type="button" onClick={closeServiceModal} aria-label="Close">
                ✕
              </button>
            </div>
            <form className="cportal-modal-body" onSubmit={handleServiceSubmit}>
              {serviceFormError && (
                <div className="cportal-alert cportal-alert-error">{serviceFormError}</div>
              )}
              <div className="cportal-form-grid">
                <div className="cportal-field">
                  <label>
                    Service name <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={serviceForm.serviceName}
                    onChange={(e) => setServiceField("serviceName", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                Status <span className="cportal-required">*</span>
              </label>
                  <select
                    value={serviceForm.status}
                    onChange={(e) => setServiceField("status", e.target.value)}
                  >
                    {SERVICE_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cportal-field cportal-field--full">
                  <label>Description</label>
                  <textarea
                    rows={3}
                    value={serviceForm.description}
                    onChange={(e) => setServiceField("description", e.target.value)}
                  />
                </div>
              </div>
              <div className="cportal-form-actions">
                <button
                  type="button"
                  className="cportal-btn cportal-btn-ghost"
                  onClick={closeServiceModal}
                  disabled={serviceSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" className="cportal-btn cportal-btn-primary" disabled={serviceSubmitting}>
                  {serviceSubmitting ? "Saving…" : editingServiceId ? "Save service" : "Add service"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showProjectModal && (
        <div className="cportal-modal-overlay" onClick={closeProjectModal} role="presentation">
          <div
            className="cportal-modal cportal-modal--form"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="client-project-modal-title"
          >
            <div className="cportal-modal-header">
              <h3 id="client-project-modal-title">
                {editingProjectId ? "Edit project" : "Add project"}
              </h3>
              <button type="button" onClick={closeProjectModal} aria-label="Close">
                ✕
              </button>
            </div>
            <form className="cportal-modal-body" onSubmit={handleProjectSubmit}>
              {projectFormError && (
                <div className="cportal-alert cportal-alert-error">{projectFormError}</div>
              )}
              <div className="cportal-form-grid">
                <div className="cportal-field">
                  <label>
                    Project name <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={projectForm.projectName}
                    onChange={(e) => setProjectField("projectName", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Short code <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={projectForm.shortCode}
                    onChange={(e) => setProjectField("shortCode", e.target.value)}
                    maxLength={20}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                Status <span className="cportal-required">*</span>
              </label>
                  <select
                    value={projectForm.status}
                    onChange={(e) => setProjectField("status", e.target.value)}
                  >
                    {PROJECT_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cportal-field cportal-field--full">
                  <label>Description</label>
                  <textarea
                    rows={3}
                    value={projectForm.description}
                    onChange={(e) => setProjectField("description", e.target.value)}
                  />
                </div>
              </div>
              <div className="cportal-form-actions">
                <button
                  type="button"
                  className="cportal-btn cportal-btn-ghost"
                  onClick={closeProjectModal}
                  disabled={projectSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" className="cportal-btn cportal-btn-primary" disabled={projectSubmitting}>
                  {projectSubmitting ? "Saving…" : editingProjectId ? "Save project" : "Add project"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default CustomerTenantClientView;
